import React, { FC, useState, useEffect } from 'react';
import { Row, Col, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import TaskItem from './TaskItem';

const Container = styled.div`
  padding-bottom: 100px;
  width: 100%;
  margin-top: 16px; /* Match RelatedCampaigns exactly */
  
  @media (max-width: 800px) {
    padding: 0;
  }
`;

const TaskHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  
  .task-heading {
    margin: 0;
    flex: 1;
  }
`;

const SortSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  
  .sort-label {
    font-size: 14px;
    font-weight: 500;
    color: var(--color-text-secondary);
    white-space: nowrap;
  }
`;

const StyledSelect = styled(Select)`
  width: 160px;
  
  .ant-select-selector {
    border-radius: 6px;
    border: 1px solid var(--color-border);
    font-size: 14px;
    
    &:hover {
      border-color: var(--color-primary);
    }
  }
  
  &.ant-select-focused .ant-select-selector {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 2px rgba(var(--color-primary-rgb), 0.1);
  }
  
  @media (max-width: 480px) {
    width: 140px;
    
    .ant-select-selector {
      font-size: 13px;
    }
  }
`;

interface Props {
  tasks: any[];
  sortBy?: string;
  onSortChange?: (sortBy: string) => void;
  hideHeading?: boolean;
}

const TaskList: FC<Props> = ({ tasks, sortBy, onSortChange, hideHeading = false }) => {
  const { t } = useTranslation();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const isMobileView = windowWidth <= 768;

  const sortOptions = [
    { value: 'task_order', label: t('#Campaign.sorting.task_order_up') },
    { value: 'expired', label: t('#Campaign.sorting.expire_time_up') },
    { value: '-expired', label: t('#Campaign.sorting.expire_time_down') },
    { value: 'miles', label: t('#Campaign.sorting.miles_up') },
    { value: '-miles', label: t('#Campaign.sorting.miles_down') },
    { value: 'task_type', label: t('#Campaign.sorting.type_up') },
    { value: '-task_type', label: t('#Campaign.sorting.type_down') }
  ];

  // Mobile view - search-like layout (following RelatedCampaigns pattern)
  if (isMobileView) {
    return (
      <Container className="task-list-container">
        {!hideHeading && (
          <TaskHeader className="task-section-header">
            <h3 className="task-heading">{t('#Campaign.tasks')}</h3>
            {sortBy && onSortChange && (
              <SortSection className="sort-section">
                <span className="sort-label">{t('#Campaign.sort_by')}</span>
                <StyledSelect
                  value={sortBy}
                  onChange={(value) => onSortChange(value as string)}
                  options={sortOptions}
                  placeholder={t('#Campaign.sort_by')}
                />
              </SortSection>
            )}
          </TaskHeader>
        )}
        
        {/* Show sorting section separately when heading is hidden */}
        {hideHeading && sortBy && onSortChange && (
          <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-end' }}>
            <SortSection className="sort-section">
              <span className="sort-label">{t('#Campaign.sort_by')}</span>
              <StyledSelect
                value={sortBy}
                onChange={(value) => onSortChange(value as string)}
                options={sortOptions}
                placeholder={t('#Campaign.sort_by')}
              />
            </SortSection>
          </div>
        )}
        
        <div className="tasks-mobile">
          {tasks.map((item, i) => (
            <TaskItem key={i} item={item} isMobile={true} />
          ))}
        </div>
      </Container>
    );
  }

  // Desktop view - card grid layout (following RelatedCampaigns pattern exactly)
  return (
    <Container className="task-list-container">
      {!hideHeading && (
        <TaskHeader className="task-section-header">
          <h3 className="task-heading">{t('#Campaign.tasks')}</h3>
          {sortBy && onSortChange && (
            <SortSection className="sort-section">
              <span className="sort-label">{t('#Campaign.sort_by')}</span>
              <StyledSelect
                value={sortBy}
                onChange={(value) => onSortChange(value as string)}
                options={sortOptions}
                placeholder={t('#Campaign.sort_by')}
              />
            </SortSection>
          )}
        </TaskHeader>
      )}
      
      {/* Show sorting section separately when heading is hidden */}
      {hideHeading && sortBy && onSortChange && (
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-end' }}>
          <SortSection className="sort-section">
            <span className="sort-label">{t('#Campaign.sort_by')}</span>
            <StyledSelect
              value={sortBy}
              onChange={(value) => onSortChange(value as string)}
              options={sortOptions}
              placeholder={t('#Campaign.sort_by')}
            />
          </SortSection>
        </div>
      )}
      
      {/* Match RelatedCampaigns structure exactly - remove intermediate container */}
      <Row gutter={[16, 16]}>
        {tasks.map((item, i) => <Col key={i} xs={24} sm={24} md={8} lg={8} xl={8}>
          <TaskItem item={item} isMobile={false} />
        </Col>)}
      </Row >
    </Container>
  );
}

export default TaskList;
